/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
    useContext,
    useState,
} from 'react';
import {
    Outlet,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { Plus } from 'lucide-react';

import Navbar from '../Navbar';
import Sidebar from '../Sidebar';
import { Button } from '../ui/button';

import AuthContext from '@/contexts/auth';
import { NotificationsProvider } from '@/contexts/notifications';
import { ChatUnreadProvider } from '@/features/chat/context';
import useAnnouncementNotifications from '@/hooks/use-announcement-notifications';
import useConnectionNotifications from '@/hooks/use-connection-notifications';
import { useIsMobile } from '@/hooks/use-mobile';
import useOrderNotifications from '@/hooks/use-order-notifications';
import useQuotationNotifications from '@/hooks/use-quotation-notifications';
import useWhatsappNotifications from '@/hooks/use-whatsapp-notifications';

function DashboardLayoutInner() {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // The Create-Order shortcut only belongs on order-centric screens; other
    // pages have their own top-right primary action.
    const showCreateOrderFab = pathname === '/dashboard'
        || pathname.startsWith('/orders');
    useConnectionNotifications();
    useAnnouncementNotifications();
    useQuotationNotifications();
    useOrderNotifications();
    useWhatsappNotifications();

    const toggleSidebar = () => {
        if (isMobile) {
            setMobileMenuOpen(!mobileMenuOpen);
        } else {
            setSidebarCollapsed(!sidebarCollapsed);
        }
    };

    const closeMobileMenu = () => {
        if (isMobile) {
            setMobileMenuOpen(false);
        }
    };

    return (
        <div className="h-screen bg-background flex">
            <Sidebar
                isCollapsed={sidebarCollapsed}
                onToggle={toggleSidebar}
                isMobileMenuOpen={mobileMenuOpen}
                onCloseMobileMenu={closeMobileMenu}
            />

            <div className="flex-1 flex flex-col overflow-hidden">
                <Navbar onToggleSidebar={toggleSidebar} />

                <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-24 md:pb-6">
                    <div className="max-w-[1600px] w-full animate-fade-in">
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* Mobile overlay */}
            {isMobile && mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-foreground/40 backdrop-blur-sm z-40 md:hidden"
                    onClick={closeMobileMenu}
                />
            )}

            {/* Floating Create Order shortcut — only on order-centric screens */}
            {showCreateOrderFab && (
                <Button
                    onClick={() => navigate('/orders/create')}
                    className="fixed bottom-6 right-6 z-30 h-12 w-12 rounded-full shadow-sm p-0"
                    aria-label="Create New Order"
                >
                    <Plus className="h-5 w-5" />
                </Button>
            )}
        </div>
    );
}

function DashboardLayout() {
    const { state } = useContext(AuthContext);
    return (
        <NotificationsProvider userId={state?.id}>
            <ChatUnreadProvider>
                <DashboardLayoutInner />
            </ChatUnreadProvider>
        </NotificationsProvider>
    );
}

export default DashboardLayout;
